import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { generateMockup } from '../lib/mockupGenerator';
import { generateMockupFromDesignSpec } from '../lib/designSpecEngine';
import { assembleLayer, getLayerTemplatesForConfig, isValidArchetype } from '../lib/templateEngine';
import { exportProject as exportProjectZip } from '../lib/fileExporter';
import {
  layer1Template,
  layer2Template,
  layer3Template,
  layer4Template,
} from '../data/projects/marveland';
import {
  deployModule,
  apiModule,
  typesModule,
  uiModule,
  pitfallsModule,
  packageManagerModule,
} from '../data/modules';
import type { ProjectConfig } from '../types';
import type { Archetype, Industry, ReferenceSite, DesignSpec, StylePreferences } from '../types/design-spec';
import { generateUUID } from '../utils/uuid';
import { analyzeReferences, type AnalyzeReferencesInput } from '../lib/aiAnalyzer';
import type { B2BSession, B2BAnswers, StreamChunk, B2BParsedFile, B2BStrategyReport } from '../types/b2b';
import { createB2BSession, getDefaultB2BAnswers } from '../types/b2b';

export type ActiveTab =
  | 'start'
  | 'review'
  | 'reference'
  | 'analysis'
  | 'designspec'
  | 'config'
  | 'mockup'
  | 'layers'
  | 'knowledge'
  | 'settings'
  | 'b2b-start'
  | 'b2b-preview'
  | 'b2b-strategy'
  | 'b2b-stream'
  | 'b2b-deploy'
  | 'b2b-gallery';

export type UiMode = 'customer' | 'expert' | 'b2b';

const modules = new Map([
  ['deploy/cloudflare-worker-pages', deployModule],
  ['api/hono-kv-auth', apiModule],
  ['types/cms-common', typesModule],
  ['ui/react-vite-tailwind', uiModule],
  ['pitfalls/cloudflare-react', pitfallsModule],
  ['package-manager/pnpm-mandatory', packageManagerModule],
]);

const fallbackLayerTemplates = [layer1Template, layer2Template, layer3Template, layer4Template];

export function getDefaultConfig(): ProjectConfig {
  const now = new Date().toISOString();
  return {
    id: generateUUID(),
    name: 'new-project',
    displayName: '新專案',
    projectType: 'landing',
    archetype: 'landing',
    industry: 'other',
    templateId: undefined,
    stylePreferences: {
      designStyle: 'modern-minimalist',
      colorScheme: 'blue-professional',
      fontPair: 'inter-noto',
      layoutStyle: 'centered-single',
    },
    language: 'zh-Hant',
    createdAt: now,
    updatedAt: now,
    version: '1.0.0',

    brand: {
      nameCn: '',
      nameEn: '',
      tagline: '',
      description: '',
      logoUrl: '',
      faviconUrl: '',
      foundedYear: '',
      licenseNumber: '',
    },

    contact: {
      phone: '',
      phoneDisplay: '',
      whatsapp: [],
      email: '',
      address: '',
      addressDisplay: '',
      businessHours: '',
      mapLink: '',
    },

    social: {
      facebook: '',
      instagram: '',
      whatsapp: '',
      wechat: '',
      linkedin: '',
      youtube: '',
    },

    design: {
      primaryColor: '#1a56db',
      secondaryColor: '#60a5fa',
      accentColor: '#f59e0b',
      backgroundColor: '#ffffff',
      textColor: '#1f2937',
      darkMode: false,
      fontFamily: 'noto-sans-tc',
      borderRadius: 8,
      shadowIntensity: 'medium',
    },

    pages: {
      home: true,
      about: true,
      services: true,
      properties: false,
      contact: true,
      blog: false,
      faq: true,
      admin: false,
    },

    hero: {
      tagline: '',
      titleLine1: '找到您的理想方案',
      titleLine2: '',
      description: '',
      primaryButtonText: '了解更多',
      primaryButtonLink: '#',
      secondaryButtonText: '聯絡我們',
      secondaryButtonLink: '#contact',
      backgroundType: 'gradient',
      backgroundValue: '',
    },

    services: [],
    team: [],
    properties: [],
    news: [],
    contentBlocks: [],
    references: [],

    tech: {
      frontend: 'react-vite-tailwind',
      backend: 'cloudflare-worker-hono',
      storage: 'kv',
      analytics: false,
      auth: 'none',
    },

    deploy: {
      workerSubdomain: 'my-landing',
      pagesProjectName: 'my-landing',
      customDomain: '',
      cloudflareAccountId: '',
    },

    customVars: {},
  };
}

function buildLayers(config: ProjectConfig): [string, string, string, string] {
  const layerTemplates = getLayerTemplatesForConfig(config) ?? fallbackLayerTemplates;
  return layerTemplates.map((template, index) => {
    return assembleLayer({
      layer: (index + 1) as 1 | 2 | 3 | 4,
      projectConfig: config,
      template,
      modules,
    }).content;
  }) as [string, string, string, string];
}

function buildMockup(config: ProjectConfig): string {
  if (config.designSpec) {
    return generateMockupFromDesignSpec(config.designSpec, config);
  }
  const sections =
    config.archetype === 'landing'
      ? ['header', 'hero', 'features', 'about', 'testimonials', 'faq', 'contact', 'footer']
      : ['header', 'hero', 'features', 'about', 'team', 'testimonials', 'contact', 'footer'];
  return generateMockup({
    projectConfig: config,
    sections,
  });
}

function mergeProjectConfigUpdates(
  current: ProjectConfig,
  partial?: Partial<ProjectConfig>
): Partial<ProjectConfig> {
  if (!partial) return {};

  const safePartial: Partial<ProjectConfig> = { ...partial };
  if (safePartial.archetype !== undefined && !isValidArchetype(safePartial.archetype)) {
    delete safePartial.archetype;
  }

  return {
    ...safePartial,
    brand: safePartial.brand ? { ...current.brand, ...safePartial.brand } : current.brand,
    contact: safePartial.contact ? { ...current.contact, ...safePartial.contact } : current.contact,
    social: safePartial.social ? { ...current.social, ...safePartial.social } : current.social,
    design: safePartial.design ? { ...current.design, ...safePartial.design } : current.design,
    pages: safePartial.pages ? { ...current.pages, ...safePartial.pages } : current.pages,
    hero: safePartial.hero ? { ...current.hero, ...safePartial.hero } : current.hero,
    tech: safePartial.tech ? { ...current.tech, ...safePartial.tech } : current.tech,
    deploy: safePartial.deploy ? { ...current.deploy, ...safePartial.deploy } : current.deploy,
    services: safePartial.services ?? current.services,
    team: safePartial.team ?? current.team,
    contentBlocks: safePartial.contentBlocks ?? current.contentBlocks,
    properties: safePartial.properties ?? current.properties,
    news: safePartial.news ?? current.news,
  };
}

function applyStylePreferences(
  designSpec: DesignSpec,
  preferences?: StylePreferences
): DesignSpec {
  if (!preferences) return designSpec;
  return {
    ...designSpec,
    style: preferences.designStyle,
    colorScheme: preferences.colorScheme,
    fontPair: preferences.fontPair,
    layoutStyle: preferences.layoutStyle,
  };
}

export interface AnalysisResult {
  rawAnalysis: string;
  designSpec: DesignSpec;
}

export interface ProjectState {
  config: ProjectConfig;
  currentProjectId: string;
  projects: ProjectConfig[];
  layers: [string, string, string, string];
  mockupHtml: string;
  activeTab: ActiveTab;
  uiMode: UiMode;
  isGenerating: boolean;
  hasUnsavedChanges: boolean;
  lastError: string | null;

  analysisLoading: boolean;
  analysisError: string | null;
  analysisResult: AnalysisResult | null;

  setConfig: (partial: Partial<ProjectConfig>) => void;
  createProject: (template?: string) => void;
  loadProject: (id: string) => void;
  deleteProject: (id: string) => void;
  generateLayers: () => void;
  generateMockup: () => void;
  exportProject: () => Promise<void>;
  setActiveTab: (tab: ActiveTab) => void;
  setUiMode: (uiMode: UiMode) => void;
  importFromJSON: (json: string) => void;
  exportToJSON: () => string;

  setReferences: (urls: ReferenceSite[]) => void;
  setArchetype: (archetype: Archetype) => void;
  setIndustry: (industry: Industry) => void;
  setDesignSpec: (designSpec: DesignSpec) => void;
  updateDesignSpec: (partial: Partial<DesignSpec>) => void;
  analyzeReferences: (input: AnalyzeReferencesInput) => Promise<void>;

  // === B2B Module ===
  b2bSession: B2BSession | null;
  b2bCurrentStep: number;

  initB2BSession: () => void;
  setB2BAnswers: (answers: Partial<B2BAnswers>) => void;
  setB2BStep: (step: number) => void;
  setB2BStatus: (status: B2BSession['status']) => void;
  setB2BStrategyReport: (report: B2BStrategyReport) => void;
  appendStreamChunk: (chunk: StreamChunk) => void;
  setB2BGeneratedFiles: (files: B2BParsedFile[]) => void;
  setB2BSystemPrompt: (prompt: string) => void;
  setB2BError: (error: string) => void;
  resetB2BSession: () => void;
  cancelB2BGeneration: () => void;
}

export const useProjectStore = create<ProjectState>()(
  persist(
    (set, get) => {
      const defaultConfig = getDefaultConfig();
      return {
        config: defaultConfig,
        currentProjectId: defaultConfig.id,
        projects: [defaultConfig],
        layers: buildLayers(defaultConfig),
        mockupHtml: buildMockup(defaultConfig),
        activeTab: 'b2b-start',
        uiMode: 'b2b',
        isGenerating: false,
        hasUnsavedChanges: false,
        lastError: null,

        analysisLoading: false,
        analysisError: null,
        analysisResult: null,

        b2bSession: null,
        b2bCurrentStep: 0,

        setConfig: (partial) => {
          set((state) => {
            const updatedConfig = {
              ...state.config,
              ...partial,
              updatedAt: new Date().toISOString(),
            };
            const updatedProjects = state.projects.map((p) =>
              p.id === updatedConfig.id ? updatedConfig : p
            );
            return {
              config: updatedConfig,
              projects: updatedProjects,
              hasUnsavedChanges: true,
            };
          });
        },

        createProject: () => {
          const newConfig = getDefaultConfig();
          newConfig.name = `project-${Date.now()}`;
          newConfig.displayName = '新專案';
          set((state) => ({
            config: newConfig,
            currentProjectId: newConfig.id,
            projects: [...state.projects, newConfig],
            layers: buildLayers(newConfig),
            mockupHtml: buildMockup(newConfig),
            activeTab: 'b2b-start',
            hasUnsavedChanges: true,
          }));
        },

        loadProject: (id) => {
          const project = get().projects.find((p) => p.id === id);
          if (!project) return;
          set({
            config: project,
            currentProjectId: project.id,
            layers: buildLayers(project),
            mockupHtml: buildMockup(project),
            hasUnsavedChanges: false,
          });
        },

        deleteProject: (id) => {
          set((state) => {
            const remaining = state.projects.filter((p) => p.id !== id);
            if (remaining.length === 0) {
              const newConfig = getDefaultConfig();
              return {
                config: newConfig,
                currentProjectId: newConfig.id,
                projects: [newConfig],
                layers: buildLayers(newConfig),
                mockupHtml: buildMockup(newConfig),
              };
            }
            const nextProject = remaining[0];
            return {
              config: nextProject,
              currentProjectId: nextProject.id,
              projects: remaining,
              layers: buildLayers(nextProject),
              mockupHtml: buildMockup(nextProject),
            };
          });
        },

        generateLayers: () => {
          set({ isGenerating: true });
          try {
            const config = get().config;
            set({
              layers: buildLayers(config),
              isGenerating: false,
              hasUnsavedChanges: true,
            });
          } catch (err) {
            set({
              isGenerating: false,
              lastError: err instanceof Error ? err.message : '生成失敗',
            });
          }
        },

        generateMockup: () => {
          set({ isGenerating: true });
          try {
            const config = get().config;
            set({
              mockupHtml: buildMockup(config),
              isGenerating: false,
              hasUnsavedChanges: true,
            });
          } catch (err) {
            set({
              isGenerating: false,
              lastError: err instanceof Error ? err.message : '生成失敗',
            });
          }
        },

        exportProject: async () => {
          const { config, layers, mockupHtml } = get();
          await exportProjectZip({
            projectName: config.name,
            layers,
            mockupHtml,
            variables: config,
          });
        },

        setActiveTab: (tab) => set({ activeTab: tab }),

        setUiMode: (uiMode) =>
          set((state) => {
            const b2bTabs: ActiveTab[] = ['b2b-start', 'b2b-preview', 'b2b-strategy', 'b2b-stream', 'b2b-deploy', 'b2b-gallery', 'settings'];
          state.activeTab = b2bTabs.includes(state.activeTab) ? state.activeTab : 'b2b-start';
            const nextTab = b2bTabs.includes(state.activeTab) ? state.activeTab : 'b2b-start';
            return { uiMode, activeTab: nextTab };
          }),

        importFromJSON: (json) => {
          try {
            const imported = JSON.parse(json) as ProjectConfig;
            if (!imported.id) imported.id = generateUUID();
            set({
              config: imported,
              currentProjectId: imported.id,
              projects: [imported, ...get().projects.filter((p) => p.id !== imported.id)],
              layers: buildLayers(imported),
              mockupHtml: buildMockup(imported),
              activeTab: 'b2b-start',
              hasUnsavedChanges: true,
            });
          } catch (err) {
            set({
              lastError: err instanceof Error ? err.message : '導入失敗',
            });
          }
        },

        exportToJSON: () => {
          return JSON.stringify(get().config, null, 2);
        },

        setReferences: (references) => {
          get().setConfig({ references });
        },

        setArchetype: (archetype) => {
          get().setConfig({ archetype });
        },

        setIndustry: (industry) => {
          get().setConfig({ industry });
        },

        setDesignSpec: (designSpec) => {
          get().setConfig({ designSpec });
        },

        updateDesignSpec: (partial) => {
          const current = get().config.designSpec;
          if (!current) {
            get().setConfig({ designSpec: partial as DesignSpec });
            return;
          }
          get().setConfig({ designSpec: { ...current, ...partial } });
        },

        analyzeReferences: async (input) => {
          set({ analysisLoading: true, analysisError: null });
          try {
            const result = await analyzeReferences(input);
            if (!result.success) {
              set({ analysisLoading: false, analysisError: result.error });
              return;
            }

            const currentConfig = get().config;
            const mergedPartial = mergeProjectConfigUpdates(currentConfig, result.projectConfig);
            const designSpecWithPreferences = applyStylePreferences(
              result.designSpec,
              currentConfig.stylePreferences
            );
            get().setConfig({ ...mergedPartial, designSpec: designSpecWithPreferences });
            get().generateLayers();
            get().generateMockup();
            get().setActiveTab('b2b-start');

            set({
              analysisLoading: false,
              analysisResult: {
                rawAnalysis: result.rawAnalysis,
                designSpec: designSpecWithPreferences,
              },
            });
          } catch (err) {
            set({
              analysisLoading: false,
              analysisError: err instanceof Error ? err.message : '分析失敗',
            });
          }
        },

        // === B2B Actions ===
        initB2BSession: () => {
          set({ b2bSession: createB2BSession(), b2bCurrentStep: 0 });
        },

        setB2BAnswers: (answers) => {
          set((state) => {
            if (!state.b2bSession) return {};
            return {
              b2bSession: {
                ...state.b2bSession,
                answers: { ...state.b2bSession.answers, ...answers },
                updatedAt: new Date().toISOString(),
              },
            };
          });
        },

        setB2BStep: (step) => set({ b2bCurrentStep: step }),

        setB2BStatus: (status) => {
          set((state) => {
            if (!state.b2bSession) return {};
            return {
              b2bSession: {
                ...state.b2bSession,
                status,
                updatedAt: new Date().toISOString(),
              },
            };
          });
        },

        setB2BStrategyReport: (report) => {
          set((state) => {
            if (!state.b2bSession) return {};
            return {
              b2bSession: {
                ...state.b2bSession,
                strategyReport: report,
                updatedAt: new Date().toISOString(),
              },
            };
          });
        },

        appendStreamChunk: (chunk) => {
          set((state) => {
            if (!state.b2bSession) return {};
            return {
              b2bSession: {
                ...state.b2bSession,
                generatedCode: state.b2bSession.generatedCode + chunk.content,
                streamLog: [...state.b2bSession.streamLog, chunk],
                updatedAt: new Date().toISOString(),
              },
            };
          });
        },

        setB2BGeneratedFiles: (files) => {
          set((state) => {
            if (!state.b2bSession) return {};
            return {
              b2bSession: {
                ...state.b2bSession,
                parsedFiles: files,
                status: 'completed',
                updatedAt: new Date().toISOString(),
              },
            };
          });
        },

        setB2BSystemPrompt: (prompt) => {
          set((state) => {
            if (!state.b2bSession) return {};
            return {
              b2bSession: {
                ...state.b2bSession,
                systemPrompt: prompt,
                updatedAt: new Date().toISOString(),
              },
            };
          });
        },

        setB2BError: (error) => {
          set((state) => {
            if (!state.b2bSession) return {};
            return {
              b2bSession: {
                ...state.b2bSession,
                status: 'error',
                error,
                updatedAt: new Date().toISOString(),
              },
            };
          });
        },

        resetB2BSession: () => {
          set({ b2bSession: createB2BSession(), b2bCurrentStep: 0 });
        },

        cancelB2BGeneration: () => {
          set((state) => {
            if (!state.b2bSession) return {};
            return {
              b2bSession: {
                ...state.b2bSession,
                status: 'cancelled',
                updatedAt: new Date().toISOString(),
              },
            };
          });
        },
      };
    },
    {
      name: 'promptforge-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        projects: state.projects,
        currentProjectId: state.currentProjectId,
        uiMode: state.uiMode,
        b2bSession: state.b2bSession
          ? {
              id: state.b2bSession.id,
              status: state.b2bSession.status,
              answers: state.b2bSession.answers,
              strategyReport: state.b2bSession.strategyReport,
              parsedFiles: state.b2bSession.parsedFiles,
              error: state.b2bSession.error,
              createdAt: state.b2bSession.createdAt,
              updatedAt: state.b2bSession.updatedAt,
            }
          : null,
        b2bCurrentStep: state.b2bCurrentStep,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        try {
          state.uiMode = 'b2b';
          const project = state.projects.find((p) => p.id === state.currentProjectId);
          if (project) {
            state.config = project;
            state.layers = buildLayers(project);
            state.mockupHtml = buildMockup(project);
          }
          const b2bTabs: ActiveTab[] = ['b2b-start', 'b2b-preview', 'b2b-strategy', 'b2b-stream', 'b2b-deploy', 'b2b-gallery', 'settings'];
          state.activeTab = b2bTabs.includes(state.activeTab) ? state.activeTab : 'b2b-start';
          state.activeTab = b2bTabs.includes(state.activeTab) ? state.activeTab : 'b2b-start';

          if (state.b2bSession) {
            const defaultAnswers = getDefaultB2BAnswers();
            state.b2bSession = {
              ...state.b2bSession,
              answers: {
                q0_identity: {
                  companyName: state.b2bSession.answers?.q0_identity?.companyName || defaultAnswers.q0_identity.companyName,
                  industry: state.b2bSession.answers?.q0_identity?.industry || defaultAnswers.q0_identity.industry,
                  description: state.b2bSession.answers?.q0_identity?.description || defaultAnswers.q0_identity.description,
                  foundedYear: state.b2bSession.answers?.q0_identity?.foundedYear || '',
                  teamSize: state.b2bSession.answers?.q0_identity?.teamSize || '',
                },
                q1_purpose: state.b2bSession.answers?.q1_purpose || defaultAnswers.q1_purpose,
                q2_audience: state.b2bSession.answers?.q2_audience || defaultAnswers.q2_audience,
                q3_products: state.b2bSession.answers?.q3_products || defaultAnswers.q3_products,
                q4_assets: {
                  logo: state.b2bSession.answers?.q4_assets?.logo,
                  images: state.b2bSession.answers?.q4_assets?.images || [],
                  brandColors: state.b2bSession.answers?.q4_assets?.brandColors || defaultAnswers.q4_assets.brandColors,
                  unsplashKeywords: state.b2bSession.answers?.q4_assets?.unsplashKeywords || '',
                },
                q5_pages: state.b2bSession.answers?.q5_pages || defaultAnswers.q5_pages,
                q6_contact: state.b2bSession.answers?.q6_contact || defaultAnswers.q6_contact,
              },
              systemPrompt: '',
              strategyPrompt: '',
              generatedCode: '',
              streamLog: [],
            } as typeof state.b2bSession;
          }
        } catch (err) {
          console.error('[projectStore] Rehydrate failed, resetting to defaults:', err);
          try {
            localStorage.removeItem('promptforge-storage');
          } catch {
            // ignore
          }
          const fresh = getDefaultConfig();
          state.config = fresh;
          state.currentProjectId = fresh.id;
          state.projects = [fresh];
          state.layers = buildLayers(fresh);
          state.mockupHtml = buildMockup(fresh);
          state.activeTab = 'b2b-start';
          state.uiMode = 'b2b';
          state.b2bSession = null;
          state.b2bCurrentStep = 0;
        }
      },
    }
  )
);
